import { postApi } from "@/utils/http";
import { isScheduleValid } from "@/utils/packageDate";
import { getPackageSearchString } from "@/utils/packageOdata";
import {
  isSamePackage,
  getPackageNameVersionAndHash,
  formSource,
  hasParam,
} from "@/utils/mdc";

import store from "@/store/index";

export async function getAllPackageVersions(items, item) {
  items = JSON.parse(JSON.stringify(items));

  let selectFirstPackage = false;

  const { name, uid } = getPackageNameVersionAndHash(item);

  const visibleAndSelectedLength = items.filter((x) => x.visible || x.selected)
    .length;

  const existing = items.find((x) => isSamePackage(x, item));
  if (existing) {
    existing.stats = item.stats;
    existing.selected = true;
    existing.visible = true;
  } else selectFirstPackage = true;

  const chocoResult = await postApi("choco/packages", {
    query: getPackageSearchString(
      name,
      10,
      visibleAndSelectedLength == 1 ? 0 : visibleAndSelectedLength
    ),
    source: formSource(item.source),
  });

  if (
    chocoResult &&
    chocoResult.packages &&
    chocoResult.packages.length !== 0
  ) {
    const chocoPackages = chocoResult.packages;

    if (selectFirstPackage && chocoPackages.length !== 0) {
      chocoPackages[0].selected = true;
    }

    const toAdd = [];

    for (const chocoItem of chocoPackages) {
      const existingPackage = items.find((x) => isSamePackage(x, chocoItem));

      if (!existingPackage) {
        chocoItem.visible = true;
        toAdd.push(chocoItem);
      } else {
        existingPackage.visible = true;
        existingPackage.chocoPackage = chocoItem.chocoPackage;
      }
    }

    items.push(...toAdd);
  }

  return orderByVersion(items);
}

export async function getAllMdcPackageVersions(computerUID, domainUID, p) {
  const { name, uid } = getPackageNameVersionAndHash(p);
  const res = await postApi("get/computerPackageAllVersions", {
    computerUIDs: [computerUID],
    domainUID: domainUID,
    sourceUID: uid,
    name: name,
  });

  return res[0].packages;
}

export async function getAllMdcGroupPackageVersions(groupUID, domainUID, p) {
  const { name, uid } = getPackageNameVersionAndHash(p);
  const res = await postApi("get/groupPackageAllVersions", {
    groupUID: groupUID,
    domainUID: domainUID,
    sourceUID: uid,
    name: name,
  });

  return res[0];
}

export function orderByVersion(packages) {
  return packages.sort((p1, p2) => {
    const { version: v1 } = getPackageNameVersionAndHash(p1);
    const { version: v2 } = getPackageNameVersionAndHash(p2);
    return compareVersions(v2, v1);
  });
}

export function compareVersions(a, b) {
  let i, diff;
  let regExStrip0 = /(\.0+)+$/;
  let segmentsA = a.replace(regExStrip0, "").split(".");
  let segmentsB = b.replace(regExStrip0, "").split(".");
  let l = Math.min(segmentsA.length, segmentsB.length);

  for (i = 0; i < l; i++) {
    diff = parseInt(segmentsA[i], 10) - parseInt(segmentsB[i], 10);
    if (diff) return diff;
  }
  return segmentsA.length - segmentsB.length;
}

export function getPackage(item, otherVersionItem) {
  const itemCopy = { ...item };

  if (itemCopy.otherVersions) {
    const existingPackage = itemCopy.otherVersions.find((x) =>
      isSamePackage(x, itemCopy)
    );

    if (existingPackage) {
      existingPackage.version = itemCopy.version;
      existingPackage.package = itemCopy.package;
      existingPackage.command = itemCopy.command;
      existingPackage.chocoPackage = itemCopy.chocoPackage;
      existingPackage.groupInfo = itemCopy.groupInfo;
      existingPackage.upgradable = itemCopy.upgradable;
    }
  }

  itemCopy.version = otherVersionItem.version;
  itemCopy.package = otherVersionItem.package;
  itemCopy.command = otherVersionItem.command;
  itemCopy.chocoPackage = otherVersionItem.chocoPackage;
  itemCopy.groupInfo = otherVersionItem.groupInfo;
  itemCopy.upgradable = false;

  const newestPackage = getNewestPackage(itemCopy.otherVersions, itemCopy);

  if (newestPackage) {
    const versionResult = compareVersions(
      itemCopy.chocoPackage.Version,
      newestPackage.chocoPackage.Version
    );

    if (versionResult > 0) {
      itemCopy.upgradable = true;
    }
  }

  itemCopy.showVersions = false;

  return itemCopy;
}

export function getNewestPackage(packages, item) {
  let newest = null;

  const { name } = getPackageNameVersionAndHash(item);

  const filtered = packages.filter(
    (x) =>
      x.package.name === name &&
      x.command.name !== "uninstall" &&
      x.command.status !== "failed" &&
      x.version.version !== "" &&
      x.source.uid === item.source.uid
  );

  if (filtered && filtered.length > 0) {
    newest = filtered[0];
  }

  let i = filtered.length;
  while (i--) {
    const currItem = filtered[i];

    if (
      newest &&
      newest.version &&
      newest.version.version &&
      currItem.version &&
      currItem.version.version
    ) {
      const result = compareVersions(
        newest.version.version,
        currItem.version.version
      );

      if (result < 0) {
        newest = currItem;
      }
    }
  }

  return newest;
}

export function addTempParam(params, param) {
  if (param.includes("|")) param = param.replace("|", "");

  if (!params) params = "";

  var arr = params.split("|");

  if (!arr.some((x) => x.trim() === param)) arr.push(param);
  else arr.splice(arr.indexOf(param), 1);

  return arr.filter((x) => x && x !== "").join("|");
}

export function removeParam(params, param) {
  if (params && params != "") {
    if (param.includes("|")) param = param.replace("|", "");
    param = param.trim();

    var arr = params.split("|");

    return arr.filter((x) => x && x.trim() !== param).join("|");
  }
}

export function findParam(params, param) {
  if (params) {
    if (param.includes("|")) param = param.replace("|", "");
    param = param.trim();

    var arr = params.split("|");
    return arr.find((x) => x.trim().startsWith(param));
  }

  return null;
}

export async function transformPackages(packages) {
  let packageInfos = packages.map((x) => getPackageNameVersionAndHash(x));
  packageInfos = [...new Set(packageInfos)];

  const promiseArr = [];

  const toReturn = {};

  for (let i = 0; i < packageInfos.length; i++) {
    let promise = new Promise((resolve) => {
      const name = packageInfos[i].name;
      const uid = packageInfos[i].uid;
      const filteredPackages = packages.filter((x) => {
        const info = getPackageNameVersionAndHash(x);
        return info.name === name && info.uid === uid;
      });

      toReturn[name + uid] = filteredPackages;
      resolve();
    });

    promiseArr.push(promise);
  }

  await Promise.all(promiseArr);
  return toReturn;
}

export function formatPackage(item, action) {
  const { hash, version, name } = getPackageNameVersionAndHash(item);
  const schedule = { ...item.tempSchedule };
  const command = { ...item.tempCommand };

  if (!isScheduleValid(schedule)) {
    schedule.startDate = null;
    schedule.endDate = null;
    schedule.startTime = null;
    schedule.endTime = null;
    schedule.type = 0;
  } else {
    schedule.startDate = schedule.startDate.toISOString();
    schedule.endDate = schedule.endDate.toISOString();
    schedule.startTime = schedule.startTime.toISOString();
    schedule.endTime = schedule.endTime.toISOString();
  }

  command.execStart = "";
  command.execDone = "";
  command.execLog = "";
  command.execUser = "";

  command.name = action;
  command.status = "pending";
  command.added = new Date().toISOString();

  return {
    schedule: schedule,
    command: command,
    version: { version: version, packageHash: hash },
    package: { name: name },
    source: item.source,
  };
}

export function upgradable(packages, p) {
  const newestPackage = getNewestPackage(packages, p);

  if (newestPackage) {
    const { version: newestVersion } = getPackageNameVersionAndHash(
      newestPackage
    );
    const { version: pVersion } = getPackageNameVersionAndHash(p);

    return compareVersions(pVersion, newestVersion) > 0;
  }

  return false;
}

export function getAllMdcVersionsFn(p, isGroup, params) {
  if (isGroup) {
    return () =>
      getAllMdcGroupPackageVersions(params.groupUID, params.domainUID, p);
  } else {
    return () =>
      getAllMdcPackageVersions(params.computerUID, params.domainUID, p);
  }
}

export async function getAllMdcVersions(p, storeState, fn) {
  const { name, uid } = getPackageNameVersionAndHash(p);

  if (!store.state[storeState].fullyLoadedPackages[name + uid]) {
    const allPackages = (await fn()).filter(
      (x) =>
        !store.state[storeState].mergedPackages[name + uid].some((y) =>
          isSamePackage(x, y)
        )
    );

    allPackages.push(p);

    return allPackages;
  }
}
